import React, { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { bookingService } from '@/services/bookingService';
import { CheckCircle, XCircle, Loader2, ArrowLeft } from 'lucide-react';

export default function PaymentSuccess() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Extract query parameters from URL
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('sessionId');
        const paymentStatus = urlParams.get('status');
        const productOptionId = urlParams.get('productOptionId');

        if (!sessionId || !paymentStatus) {
          throw new Error(t('missingPaymentParameters') || 'معاملات الدفع مفقودة');
        }

        // Call payment success API
        const response = await bookingService.confirmPaymentSuccess({
          sessionId,
          status: paymentStatus,
          productOptionId: productOptionId || undefined,
        });

        if (response.success) {
          setStatus('success');
          setMessage(response.message || t('paymentVerifiedSuccessfully') || 'تم التحقق من الدفع بنجاح');
          
          toast({
            title: t('success') || 'نجح',
            description: t('paymentVerifiedSuccessfully') || 'تم التحقق من الدفع بنجاح',
          });

          // Redirect to bookings page after 2 seconds
          setTimeout(() => {
            setLocation('/bookings');
          }, 2000);
        } else {
          throw new Error(response.message || t('paymentVerificationFailed') || 'فشل التحقق من الدفع');
        }
      } catch (error: any) {
        console.error('Payment verification error:', error);
        setStatus('error');
        setMessage(error.message || t('paymentVerificationFailed') || 'فشل التحقق من الدفع');
        
        toast({
          title: t('error') || 'خطأ',
          description: error.message || t('paymentVerificationFailed') || 'فشل التحقق من الدفع',
          variant: 'destructive',
        });
      }
    };

    verifyPayment();
  }, [t, toast, setLocation]);

  const handleGoToBookings = () => {
    setLocation('/bookings');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-muted/30">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">
            {status === 'loading' && (
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
                <span>{t('verifyingPayment') || 'جاري التحقق من الدفع...'}</span>
              </div>
            )}
            {status === 'success' && (
              <div className="flex flex-col items-center gap-4">
                <CheckCircle className="h-12 w-12 text-green-500" />
                <span className="text-green-600">{t('paymentSuccessful') || 'تم الدفع بنجاح'}</span>
              </div>
            )}
            {status === 'error' && (
              <div className="flex flex-col items-center gap-4">
                <XCircle className="h-12 w-12 text-red-500" />
                <span className="text-red-600">{t('paymentFailed') || 'فشل الدفع'}</span>
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {message && (
            <p className="text-center text-muted-foreground">{message}</p>
          )}
          
          {status === 'success' && (
            <p className="text-center text-sm text-muted-foreground">
              {t('redirectingToBookings') || 'سيتم إعادة التوجيه إلى صفحة الحجوزات...'}
            </p>
          )}

          <div className="flex justify-center gap-4 pt-4">
            <Button
              onClick={handleGoToBookings}
              variant={status === 'error' ? 'default' : 'outline'}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('goToBookings') || 'العودة إلى الحجوزات'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

